import React, { useState, useEffect } from 'react';
import { getAuthHeadersSync } from '../../../../shared/utils/authHelpers';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';
import { 
  Activity, AlertTriangle, CheckCircle2, RefreshCw, 
  Shield, Zap, AlertCircle, XCircle, Clock
} from 'lucide-react';

interface RateLimitInfo {
  limit: number | null;
  remaining: number | null;
  reset_at: string | null;
  last_updated: string | null;
}

interface ThreatFeed {
  id: string;
  tenant_id: string;
  name: string;
  provider: string;
  status: 'active' | 'inactive' | 'syncing' | 'error';
  last_sync_at: string | null;
  next_sync_at: string | null;
  indicators_count: number;
  error_count: number;
  last_error: string | null;
  polling_interval_minutes: number;
  rate_limit: RateLimitInfo | null;
}

interface FeedHealthResponse {
  summary: {
    total_feeds: number;
    active_feeds: number;
    error_feeds: number;
    syncing_feeds: number;
    total_indicators: number;
  };
  feeds: ThreatFeed[];
}

export const ThreatIntelFeeds: React.FC = () => {
  const [feedHealth, setFeedHealth] = useState<FeedHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncingFeeds, setSyncingFeeds] = useState<Set<string>>(new Set());

  const fetchFeedHealth = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getBaseUrl()}/api/admin/threat-intel/feeds/health`, {
        method: 'GET',
        headers: getAuthHeadersSync()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch feed health: ${response.statusText}`);
      }

      const data = await response.json();
      setFeedHealth(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch feed health');
      console.error('Error fetching feed health:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedHealth();
    // Refresh every 30 seconds
    const interval = window.setInterval(fetchFeedHealth, 30000);
    return () => {
      window.clearInterval(interval);
    };
  }, []);

  const handleManualSync = async (feedId: string) => {
    try {
      setSyncingFeeds(prev => new Set(prev).add(feedId));
      
      const response = await fetch(`${getBaseUrl()}/api/admin/threat-intel/feeds/${feedId}/sync?force=true`, {
        method: 'POST',
        headers: getAuthHeadersSync()
      });

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Sync result:', result);
      
      // Refresh feed health after sync
      await fetchFeedHealth();
    } catch (err) {
      console.error('Error syncing feed:', err);
      alert(err instanceof Error ? err.message : 'Failed to sync feed');
    } finally {
      setSyncingFeeds(prev => {
        const newSet = new Set(prev);
        newSet.delete(feedId);
        return newSet;
      });
    }
  };

  const formatRelativeTime = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 0) return 'Overdue';
    if (diffMins === 0) return 'Now';
    if (diffMins < 60) return `in ${diffMins} min`;
    
    const diffHours = Math.round(diffMins / 60);
    return `in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  };

  const formatLastSync = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    
    const diffHours = Math.round(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    
    const diffDays = Math.round(diffHours / 24);
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  const getRateLimitStatus = (rateLimit: RateLimitInfo | null): { text: string; color: string } => {
    if (!rateLimit || rateLimit.limit === null || rateLimit.remaining === null) {
      return { text: 'Unknown', color: 'text-gray-500' };
    }
    
    const percentage = (rateLimit.remaining / rateLimit.limit) * 100;
    
    if (percentage > 50) {
      return { text: 'Healthy', color: 'text-green-600' };
    } else if (percentage > 20) {
      return { text: 'Warning', color: 'text-orange-600' };
    } else {
      return { text: 'Critical', color: 'text-red-600' };
    }
  };

  const formatErrorMessage = (error: string): string => {
    // Extract user-friendly error from PostgreSQL error
    if (error.includes('UniqueViolation')) {
      return 'Duplicate indicators detected (will be handled on next sync)';
    }
    if (error.includes('NotNullViolation') && error.includes('indicator_value')) {
      return 'Invalid data from feed source (missing indicator values)';
    }
    if (error.includes('rate limit')) {
      return 'API rate limit exceeded - will retry later';
    }
    if (error.includes('timeout')) {
      return 'Feed source timeout - will retry later';
    }
    if (error.includes('authentication') || error.includes('401')) {
      return 'Authentication failed - check API key';
    }
    
    // If error is short enough, show it
    if (error.length < 150) {
      return error;
    }
    
    // Otherwise, show first line only
    const firstLine = error.split('\n')[0];
    return firstLine.length < 150 ? firstLine : 'Sync error - check logs for details';
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      inactive: 'bg-red-500/20 text-red-400 border-red-500/30',
      syncing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      error: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle2 className="h-4 w-4 text-green-400" />;
      case 'syncing':
        return <RefreshCw className="h-4 w-4 text-blue-400 animate-spin" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-400" />;
      case 'inactive':
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
      default:
        return <Activity className="h-4 w-4 text-gray-400" />;
    }
  };

  if (loading && !feedHealth) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading threat intel feed health...</p>
        </div>
      </div>
    );
  }

  if (error && !feedHealth) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <h3 className="text-lg font-semibold text-red-400">Error Loading Feed Health</h3>
          </div>
          <p className="text-red-400/80">{error}</p>
          <button
            onClick={fetchFeedHealth}
            className="mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white mb-2">Threat Intelligence Feed Management</h1>
          <p className="text-gray-400">Platform-wide feed health and synchronization controls</p>
        </div>
        <button
          onClick={fetchFeedHealth}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors border border-gray-700"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Overview Cards */}
      {feedHealth && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="h-5 w-5 text-green-400" />
              <span className="text-sm text-gray-400">Active Feeds</span>
            </div>
            <div className="text-2xl font-semibold text-white">
              {feedHealth.summary.active_feeds}/{feedHealth.summary.total_feeds}
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Activity className="h-5 w-5 text-blue-400" />
              <span className="text-sm text-gray-400">Total Indicators</span>
            </div>
            <div className="text-2xl font-semibold text-white">
              {feedHealth.summary.total_indicators.toLocaleString()}
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center gap-3 mb-2">
              <RefreshCw className="h-5 w-5 text-blue-400" />
              <span className="text-sm text-gray-400">Syncing</span>
            </div>
            <div className="text-2xl font-semibold text-blue-400">
              {feedHealth.summary.syncing_feeds}
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <span className="text-sm text-gray-400">Error Feeds</span>
            </div>
            <div className="text-2xl font-semibold text-red-400">
              {feedHealth.summary.error_feeds}
            </div>
          </div>
        </div>
      )}

      {/* Rate Limit Warnings */}
      {feedHealth && feedHealth.feeds.some(f => {
        const rl = f.rate_limit;
        return rl && rl.limit !== null && rl.remaining !== null && (rl.remaining / rl.limit) < 0.2;
      }) && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <h3 className="text-sm font-semibold text-yellow-400">Rate Limit Warnings</h3>
          </div>
          <div className="space-y-2">
            {feedHealth.feeds.filter(f => {
              const rl = f.rate_limit;
              return rl && rl.limit !== null && rl.remaining !== null && (rl.remaining / rl.limit) < 0.2;
            }).map(feed => {
              const rl = feed.rate_limit!;
              const percentage = ((rl.remaining! / rl.limit!) * 100).toFixed(1);
              return (
                <div key={feed.id} className="text-sm text-yellow-400/80">
                  <strong className="text-yellow-400">{feed.name}:</strong> {rl.remaining}/{rl.limit} requests remaining ({percentage}%)
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Feed Cards */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">AstraPeak Shared Feeds</h2>
        
        {feedHealth && feedHealth.feeds.map(feed => {
          const isSyncing = syncingFeeds.has(feed.id) || feed.status === 'syncing';
          const rateLimitStatus = getRateLimitStatus(feed.rate_limit);
          
          return (
            <div key={feed.id} className="bg-gray-800 rounded-lg border border-gray-700 p-6 hover:border-gray-600 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{feed.name}</h3>
                    <span className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border ${getStatusBadge(feed.status)}`}>
                      {getStatusIcon(feed.status)}
                      {feed.status}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-400 mb-4">{feed.provider}</p>
                  
                  <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-blue-400" />
                      <div>
                        <p className="text-xs text-gray-400">Indicators</p>
                        <p className="font-semibold text-white">
                          {feed.indicators_count.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-400" />
                      <div>
                        <p className="text-xs text-gray-400">Errors</p>
                        <p className={`font-semibold ${feed.error_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {feed.error_count}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-purple-400" />
                      <div>
                        <p className="text-xs text-gray-400">Last Sync</p>
                        <p className="font-semibold text-white">
                          {formatLastSync(feed.last_sync_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {feed.next_sync_at && (
                    <div className="flex items-center gap-2 text-xs text-gray-400 pb-3 border-b border-gray-700">
                      <RefreshCw className="h-3.5 w-3.5" />
                      Next sync: {formatRelativeTime(feed.next_sync_at)}
                    </div>
                  )}
                  
                  {feed.rate_limit && feed.rate_limit.limit !== null && feed.rate_limit.remaining !== null && (
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <Activity className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-400">Rate limit:</span>
                      <span className={`font-medium ${rateLimitStatus.color === 'text-green-600' ? 'text-green-400' : rateLimitStatus.color === 'text-orange-600' ? 'text-yellow-400' : 'text-red-400'}`}>
                        {feed.rate_limit.remaining}/{feed.rate_limit.limit} ({rateLimitStatus.text})
                      </span>
                    </div>
                  )}
                  
                  {feed.last_error && (
                    <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <span className="text-red-400">Last error: {formatErrorMessage(feed.last_error)}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => handleManualSync(feed.id)}
                  disabled={isSyncing}
                  className={`ml-4 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isSyncing
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-primary hover:bg-primary/80 text-white'
                  }`}
                >
                  <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? 'Syncing...' : 'Sync Now'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Notes */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="h-4 w-4 text-blue-400" />
          <h3 className="text-sm font-semibold text-blue-400">Important Notes</h3>
        </div>
        <ul className="text-sm text-blue-400/80 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Feeds are shared across all tenants</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Manual sync affects all tenant environments</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Rate limits are platform-wide quotas</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Auto-sync runs every 15 minutes for active feeds</span>
          </li>
        </ul>
      </div>
    </div>
  );
};
